import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ComingSoonOverlay from '../components/ComingSoonOverlay';
import SEO, { generateOrganizationSchema } from '../components/SEO';
import usePageTitle from '../hooks/usePageTitle';
import { Check, ChevronLeft, ChevronRight, ArrowRight, Sparkles } from 'lucide-react';
import { PRE_LAUNCH_MODE } from '../config/preLaunch';

const IMG = '/images/landing';

// Peach pill CTA used across the page (matches Figma "→ TEXT" buttons)
const CTA = ({ children, onClick, variant = 'peach', className = '' }) => {
  const styles =
    variant === 'navy'
      ? 'bg-navy text-white hover:bg-navy-light'
      : 'bg-peach text-navy hover:bg-peach-dark';
  return (
    <button
      onClick={onClick}
      className={`group inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold uppercase tracking-wide text-sm transition-all ${styles} ${className}`}
    >
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      {children}
    </button>
  );
};

const LandingPageNew = ({ user, onLogout }) => {
  const navigate = useNavigate();
  usePageTitle('Event-Dienstleister finden');

  // Testimonial carousel (slides through the set, pause on hover)
  const testimonials = [
    { quote: 'In 10 Minuten den perfekten Fotografen gefunden.', name: 'Anna M.', role: 'Geburtstagskind' },
    { quote: 'Seit Eventconnect kommen die Anfragen zu mir – nicht mehr umgekehrt.', name: 'Tom K.', role: 'Gitarrist' },
    { quote: 'Endlich eine Plattform, der ich wirklich vertraue.', name: 'Julia S.', role: 'Projektleiterin' },
    { quote: 'Unser DJ war der Wahnsinn – die Buchung war in wenigen Minuten erledigt.', name: 'Markus B.', role: 'Bräutigam' },
    { quote: 'Endlich Anfragen, die wirklich zu mir und meinem Stil passen.', name: 'Lena F.', role: 'Fotografin' },
    { quote: 'Alles an einem Ort – von Catering bis Musik. Eine riesige Zeitersparnis.', name: 'David R.', role: 'Eventplaner' },
  ];

  const [isPaused, setIsPaused] = useState(false);
  const [perView, setPerView] = useState(3);
  const [index, setIndex] = useState(0);

  // Responsive: how many cards are visible at once
  useEffect(() => {
    const update = () => setPerView(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - perView);

  // Keep index in range when the viewport (and perView) changes
  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const nextTestimonial = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  const prevTestimonial = useCallback(() => {
    setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextTestimonial, 4000);
    return () => clearInterval(timer);
  }, [isPaused, nextTestimonial]);

  const trust = [
    {
      title: 'Feiern soll Freude machen.',
      desc: 'Von der ersten Idee bis zum letzten Konfetti – wir sorgen dafür, dass du dich auf das Wesentliche konzentrieren kannst.',
    },
    {
      title: 'Ein Ort für alle',
      desc: 'Feiernde und Eventpros finden hier zusammen – schnell, vertrauensvoll und auf Augenhöhe.',
    },
    {
      title: 'Kein Marketing-Sprech',
      desc: 'Wie ein Tipp von der besten Freundin – ehrlich, direkt und auf den Punkt.',
    },
    {
      title: 'Vertrauen durch Transparenz',
      desc: 'Echte Bewertungen, verifizierte Profile und ein klares Empfehlungsprinzip machen Qualität sichtbar.',
    },
  ];

  const categories = [
    { label: 'Hochzeit', img: `${IMG}/wedding-group.jpg` },
    { label: 'Geburtstag', img: `${IMG}/desserts.jpg` },
    { label: 'Firmenevent', img: `${IMG}/catering.jpg` },
    { label: 'JGA', img: `${IMG}/singer.jpg` },
    { label: 'Taufe', img: `${IMG}/table-banquet.jpg` },
    { label: 'Jubiläum', img: `${IMG}/table-lights.jpg` },
    { label: 'Gender Reveal', img: `${IMG}/ringlight.jpg` },
    { label: 'Abiparty', img: `${IMG}/dancefloor.jpg` },
  ];

  const steps = {
    feiernde: [
      { t: 'Event & Kategorie wählen', d: 'Sag uns, was du planst und welche Dienstleister du suchst.' },
      { t: 'Eventpros vergleichen', d: 'Schaue Profile, Bewertungen und Referenzen an.' },
      { t: 'Anfrage senden', d: 'Kontaktiere passende Anbieter direkt und kostenlos.' },
    ],
    eventpros: [
      { t: 'Profil erstellen', d: 'Zeige deine Leistungen, Referenzen und Preise.' },
      { t: 'Plan wählen', d: 'Ab 16,99 €/Monat – monatlich kündbar, 0% Provision.' },
      { t: 'Anfragen erhalten', d: 'Werde gefunden und erhalte qualifizierte Anfragen.' },
    ],
  };

  return (
    <div className="min-h-screen bg-navy text-white">
      <SEO
        title="Event-Dienstleister finden"
        description="Die Plattform für Event-Dienstleister in Deutschland. Finde oder biete Event-Dienstleistungen – von DJ über Fotograf bis Catering. Kostenlos für Kunden, ab 16,99€/Monat für Dienstleister."
        keywords="Event Dienstleister, Hochzeit, DJ, Fotograf, Catering, Event Services, Deutschland"
        url="/"
        structuredData={generateOrganizationSchema()}
      />
      <Navbar user={user} onLogout={onLogout} />

      {PRE_LAUNCH_MODE && !(user && user.role === 'admin') && <ComingSoonOverlay />}

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden pt-24 sm:pt-28 pb-16">
        {/* Playful decorative circle group (sits behind the hero images) */}
        <img
          src="/images/hero-squiggle.svg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-16 -left-28 w-[720px] max-w-[70%] opacity-70 hidden md:block"
        />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Photo collage */}
          <div className="relative w-full aspect-[5/4] sm:aspect-[4/3]">
            <img
              src={`${IMG}/hero-dj.jpg`}
              alt="DJ bei einem Event"
              className="absolute top-0 left-0 w-[72%] h-[78%] object-cover rounded-[24px] shadow-2xl"
            />
            <img
              src={`${IMG}/party-crowd.jpg`}
              alt="Feiernde Menschen"
              className="absolute bottom-0 right-0 w-[62%] h-[52%] object-cover rounded-[24px] shadow-2xl ring-4 ring-navy"
            />
          </div>

          {/* Headline + CTA */}
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-8">
              Wir glauben daran, dass Feiern Freude machen soll &ndash; von der ersten Idee bis zum letzten Konfetti.
            </h1>
            <CTA onClick={() => navigate('/search')}>Jetzt Event planen</CTA>
          </div>
        </div>
      </section>

      {/* ===== Trust / Value ===== */}
      <section className="px-4 sm:px-8 py-8 sm:py-12">
        <div className="max-w-[1400px] mx-auto bg-[#ededed] rounded-[40px] px-6 sm:px-12 lg:px-16 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy leading-snug mb-12 max-w-4xl">
            Eventconnect bringt Feiernde und Eventpros zusammen. Schnell, vertrauensvoll und auf Augenhöhe.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {trust.map((item, idx) => (
              <div key={idx}>
                <div className="w-14 h-14 bg-peach rounded-full flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-navy" />
                </div>
                <h3 className="font-bold text-navy text-base mb-3 leading-snug">{item.title}</h3>
                <p className="text-navy/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Dein Event ===== */}
      <section className="px-4 sm:px-8 py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Text */}
          <div className="order-2 lg:order-1 max-w-lg">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">Dein Event</h2>
            <p className="text-2xl sm:text-3xl font-bold text-white mb-8">
              beginnt nicht mit <span className="line-through decoration-2">Stress</span>. Sondern{' '}
              <span className="underline decoration-2 underline-offset-4">hier</span>.
            </p>
            <ul className="space-y-3 mb-10">
              {['Kostenlos', 'Verifizierte Eventpros', 'Alle Anlässe', 'Kein Account nötig. Einfach loslegen.'].map((text, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-peach flex-shrink-0" />
                  <span className="text-white/90">{text}</span>
                </li>
              ))}
            </ul>
            <CTA onClick={() => navigate('/search')}>Jetzt entdecken</CTA>
          </div>

          {/* Photo collage */}
          <div className="order-1 lg:order-2 relative w-full aspect-square">
            <img src={`${IMG}/party-crowd.jpg`} alt="Feier" className="absolute top-0 right-0 w-[72%] h-[42%] object-cover rounded-[24px] shadow-2xl" />
            <img src={`${IMG}/wedding-dance.jpg`} alt="Hochzeit" className="absolute top-[30%] left-0 w-[50%] h-[46%] object-cover rounded-[24px] shadow-2xl ring-4 ring-navy" />
            <img src={`${IMG}/catering.jpg`} alt="Catering" className="absolute bottom-0 right-[4%] w-[48%] h-[46%] object-cover rounded-[24px] shadow-2xl ring-4 ring-navy" />
          </div>
        </div>
      </section>

      {/* ===== Eventpros ===== */}
      <section className="px-4 sm:px-8 py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Photo collage */}
          <div className="relative w-full aspect-square">
            <img src={`${IMG}/guitarist.jpg`} alt="Musiker" className="absolute top-0 left-[14%] w-[64%] h-[44%] object-cover rounded-[24px] shadow-2xl" />
            <img src={`${IMG}/party-crowd.jpg`} alt="Feier" className="absolute top-[34%] left-0 w-[44%] h-[44%] object-cover rounded-[24px] shadow-2xl ring-4 ring-navy" />
            <img src={`${IMG}/photographer.jpg`} alt="Fotografin" className="absolute bottom-0 left-[24%] w-[48%] h-[52%] object-cover rounded-[24px] shadow-2xl ring-4 ring-navy" />
          </div>

          {/* Text */}
          <div className="max-w-lg">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">Eventpros</h2>
            <p className="text-2xl sm:text-3xl font-bold text-white mb-8">
              Dein Talent verdient eine Bühne.{' '}
              <span className="underline decoration-2 underline-offset-4">Hier</span> ist sie.
            </p>
            <ul className="space-y-3 mb-10">
              {['Ab 16,99 €/Monat', 'Anfragen kommen zu dir', 'Profil in Minuten'].map((text, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-peach flex-shrink-0" />
                  <span className="text-white/90">{text}</span>
                </li>
              ))}
            </ul>
            <CTA onClick={() => navigate('/register?role=provider')}>Profil anlegen</CTA>
          </div>
        </div>
      </section>

      {/* ===== So funktioniert's ===== */}
      <section className="px-4 sm:px-8 py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-white mb-12">
            So funktioniert&rsquo;s:
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
            {/* Für Feiernde */}
            <div className="bg-[#FCE4CD] rounded-3xl p-8 sm:p-12 flex flex-col">
              <h3 className="text-3xl font-bold text-navy mb-8">Für Feiernde</h3>
              <div className="space-y-6 flex-1">
                {steps.feiernde.map((s, i) => (
                  <div key={i}>
                    <p className="font-bold text-navy text-lg mb-1">
                      <span className="mr-2">{i + 1}.</span>{s.t}
                    </p>
                    <p className="text-navy/70 text-sm pl-6">{s.d}</p>
                  </div>
                ))}
              </div>
              <div className="pt-8 flex justify-center">
                <CTA variant="navy" onClick={() => navigate('/kategorien')}>Eventideen</CTA>
              </div>
            </div>

            {/* Für Eventpros */}
            <div className="bg-[#ededed] rounded-3xl p-8 sm:p-12 flex flex-col">
              <h3 className="text-3xl font-bold text-navy mb-8">Für Eventpros</h3>
              <div className="space-y-6 flex-1">
                {steps.eventpros.map((s, i) => (
                  <div key={i}>
                    <p className="font-bold text-navy text-lg mb-1">
                      <span className="mr-2">{i + 1}.</span>{s.t}
                    </p>
                    <p className="text-navy/70 text-sm pl-6">{s.d}</p>
                  </div>
                ))}
              </div>
              <div className="pt-8 flex justify-center">
                <CTA onClick={() => navigate('/register?role=provider')}>Profil anlegen</CTA>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Inspo / Category Grid ===== */}
      <section className="px-4 sm:px-8 py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              <span className="text-peach line-through decoration-2">Scroll nicht durch Instagram.</span>
              <br />
              <span className="text-white">Scroll durch Möglichkeiten.</span>
            </h2>
            <CTA onClick={() => navigate('/kategorien')} className="flex-shrink-0">Alle Anlässe entdecken</CTA>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((cat, idx) => (
              <button
                key={idx}
                onClick={() => navigate(`/search?category=${encodeURIComponent(cat.label)}`)}
                className="text-left group"
              >
                <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-3">
                  <img
                    src={cat.img}
                    alt={cat.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <span className="text-white font-semibold text-sm sm:text-base">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section
        className="px-4 sm:px-8 py-16 sm:py-24"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-end justify-between gap-4 mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              <span className="text-peach">Andere haben bereits gefeiert.</span>
              <br />
              <span className="text-white">Jetzt bist du dran.</span>
            </h2>
            <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={prevTestimonial}
                aria-label="Vorherige Bewertung"
                className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:text-white hover:border-white/60 active:scale-90 transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={nextTestimonial}
                aria-label="Nächste Bewertung"
                className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:text-white hover:border-white/60 active:scale-90 transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                width: `${(testimonials.length / perView) * 100}%`,
                transform: `translateX(-${index * (100 / testimonials.length)}%)`,
              }}
            >
              {testimonials.map((t, idx) => (
                <div key={idx} className="px-3" style={{ width: `${100 / testimonials.length}%` }}>
                  <div className="bg-peach rounded-3xl p-8 text-navy flex flex-col h-full min-h-[280px]">
                    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center font-bold text-navy mb-auto">
                      {t.name.charAt(0)}
                    </div>
                    <p className="text-base sm:text-lg font-semibold mt-6 mb-4 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                    <p className="text-sm text-navy/80">&ndash; {t.name}, {t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setIndex(idx)}
                className={`h-2.5 rounded-full transition-all ${idx === index ? 'w-6 bg-peach' : 'w-2.5 bg-white/30'}`}
                aria-label={`Bewertung ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pre-Footer CTA ===== */}
      <section className="px-4 sm:px-8 pb-16 sm:pb-24">
        <div className="max-w-[1400px] mx-auto relative overflow-hidden rounded-[40px]">
          <img src={`${IMG}/catering.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center px-8 sm:px-14 py-16 sm:py-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Dein perfektes<br />Event wartet:
            </h2>
            <div className="space-y-8">
              <button onClick={() => navigate('/search')} className="group w-full flex items-center justify-between text-left gap-4">
                <div>
                  <span className="text-xl sm:text-2xl font-bold text-white block mb-1">Mein Event planen</span>
                  <span className="text-white/70 text-sm">Mehr Vorfreude statt Planungsstress.</span>
                </div>
                <ArrowRight className="w-8 h-8 text-white flex-shrink-0 group-hover:translate-x-2 transition-transform" />
              </button>
              <div className="border-t border-white/15" />
              <button onClick={() => navigate('/register?role=provider')} className="group w-full flex items-center justify-between text-left gap-4">
                <div>
                  <span className="text-xl sm:text-2xl font-bold text-white block mb-1">Als Eventpro durchstarten</span>
                  <span className="text-white/70 text-sm">Anfragen kommen zu dir &ndash; nicht umgekehrt.</span>
                </div>
                <ArrowRight className="w-8 h-8 text-white flex-shrink-0 group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPageNew;
